import { supabase } from './supabase';
import { getDB } from '../utils/crypto';
import { addToSyncQueue } from '../utils/sync';

// Helper function to extract message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

// Define types for group operations
export interface Group {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  created_by: string;
}

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  username: string; // Join with users table
  joined_at: string;
  invited_by: string | null;
}

export interface GroupInvite {
  id: string;
  group_id: string;
  code: string;
  expires_at: string;
  created_at: string;
  is_active: boolean;
}

export interface GroupPicture {
  id: string;
  group_id: string;
  uploaded_by: string;
  file_path: string;
  file_size: number;
  uploaded_at: string;
  description: string | null;
}

export interface GroupProgress {
  id: string;
  group_id: string;
  qr_number: number;  // Changed from question_number to match database
  scanned_by: string;
  scanned_by_username: string | null; // Joined with users table, can be null if user not found
  answered_by: string | null;
  answered_by_username: string | null; // Joined with users table
  scanned_at: string;
  answered_at: string | null;
  answer: string | null;
}

// Initialize IndexedDB with group-related stores
export const initGroupDB = async () => {
  const db = await getDB();
  if (!db) return null;
  
  // These stores are already created in the crypto.ts initDB function
  // Just return the DB instance
  return db;
};

// Create a new group
export const createGroup = async (name: string, description: string | null = null): Promise<{ success: boolean; group_id?: string; error?: string }> => {
  try {
    // Validate group name on client side
    if (!name || name.trim().length < 3 || name.trim().length > 50) {
      return { success: false, error: 'Gruppenname muss zwischen 3 und 50 Zeichen lang sein' };
    }
    
    // Call the SQL function to create a group
    const { data, error } = await (supabase.rpc as any)('create_and_join_group', {
      group_name: name.trim(),
      group_description: description
    });
    
    if (error) {
      console.error('Error from create_and_join_group:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('duplicate key')) {
        return { success: false, error: 'Eine Gruppe mit diesem Namen existiert bereits' };
      } else if (error.message && error.message.includes('already a member of a group')) {
        return { success: false, error: 'Du bist bereits Mitglied einer Gruppe. Verlasse deine aktuelle Gruppe, bevor du eine neue erstellst.' };
      }
      
      throw error;
    }
    
    const groupId = data as string;
    
    // Cache group locally for offline access
    await cacheGroupLocally(groupId);
    return { success: true, group_id: groupId };
  } catch (error: unknown) {
    console.error('Error creating group:', error);
    return { success: false, error: getErrorMessage(error) || 'Fehler beim Erstellen der Gruppe' };
  }
};

// Get a specific group by ID
export const getGroup = async (groupId: string): Promise<Group | null> => {
  try {
    // Try to get from local IndexedDB first
    const db = await getDB();
    if (db) {
      const localGroup = await db.get('groups', groupId);
      if (localGroup) return localGroup;
    }
    
    // If not found locally, fetch from Supabase
    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .single();
      
    if (error) throw error;
    
    // Cache the group locally
    if (data && db) {
      await db.put('groups', data);
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching group ${groupId}:`, error);
    return null;
  }
};

// Get all groups the current user is a member of
export const getUserGroups = async (): Promise<Group[]> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    
    // First, get the group IDs the user is a member of
    const { data: memberships, error: memberError } = await (supabase
      .from('group_members') as any)
      .select('group_id')
      .eq('user_id', user.id);
    
    if (memberError) {
      console.error('Error fetching group memberships:', memberError);
      
      // Return cached data immediately if there's an error
      const db = await getDB();
      if (db) {
        const cachedMemberships = await db.getAllFromIndex('group_members', 'by_user_id', user.id);
        if (cachedMemberships && cachedMemberships.length > 0) {
          const groupPromises = cachedMemberships.map(m => db.get('groups', m.group_id));
          const groups = await Promise.all(groupPromises);
          return groups.filter(g => !!g);
        }
      }
      return [];
    }
    
    if (!memberships || memberships.length === 0) return [];
    
    // Then fetch the actual group details
    const groupIds = memberships.map((m: any) => m.group_id);
    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .in('id', groupIds);
      
    if (error) throw error;
    
    // Cache groups locally
    const db = await getDB();
    if (db && data) {
      // Use transaction for atomicity
      const tx = db.transaction('groups', 'readwrite');
      const groupsStore = tx.objectStore('groups');
      
      for (const group of data) {
        await groupsStore.put(group);
      }
      
      await tx.done;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching user groups:', error);
    
    // Return cached groups without making additional network calls
    try {
      const db = await getDB();
      if (db) {
        // Get all cached groups (don't try to fetch user again)
        const allGroups = await db.getAll('groups');
        return allGroups || [];
      }
    } catch (localError) {
      console.error('Error fetching local groups:', localError);
    }
    
    return [];
  }
};

// Generate an invite for a group
export const generateGroupInvite = async (groupId: string): Promise<{ success: boolean; code?: string; error?: string }> => {
  try {
    // Call the SQL function to generate an invite
    const { data, error } = await (supabase.rpc as any)('generate_group_invite', {
      group_id: groupId,
      expires_in_days: 7
    });
    
    if (error) {
      console.error('Error from generate_group_invite:', error);
      
      if (error.message && error.message.includes('not a member')) {
        return { success: false, error: 'Du bist kein Mitglied dieser Gruppe' };
      }
      
      throw error;
    }
    
    const inviteCode = data as string;
    
    // Cache invite locally
    const db = await getDB();
    if (db) {
      await db.put('group_invites', {
        group_id: groupId,
        code: inviteCode,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        created_at: new Date().toISOString(),
        is_active: true
      });
    }
    
    return { success: true, code: inviteCode };
  } catch (error: unknown) {
    console.error('Error generating invite:', error);
    return { success: false, error: getErrorMessage(error) || 'Failed to generate invite' };
  }
};

// Join a group using an invite code
export const joinGroupByInvite = async (inviteCode: string): Promise<{ success: boolean; group_id?: string; error?: string }> => {
  try {
    // Call the SQL function to join a group
    const { data, error } = await (supabase.rpc as any)('join_group_by_invite', {
      invite_code: inviteCode
    });
    
    if (error) {
      console.error('Error from join_group_by_invite:', error);
      
      if (error.message && error.message.includes('Invalid or expired')) {
        return { success: false, error: 'Ungültiger oder abgelaufener Einladungscode' };
      } else if (error.message && error.message.includes('already a member of a group')) {
        return { success: false, error: 'Du bist bereits Mitglied einer Gruppe. Verlasse deine aktuelle Gruppe, bevor du einer neuen beitrittst.' };
      } else if (error.message && error.message.includes('already a member')) {
        return { success: false, error: 'Du bist bereits Mitglied dieser Gruppe' };
      }
      
      throw error;
    }
    
    const groupId = data as string;
    
    // Cache group locally
    await cacheGroupLocally(groupId);
    return { success: true, group_id: groupId };
  } catch (error: unknown) {
    console.error('Error joining group:', error);
    return { success: false, error: getErrorMessage(error) || 'Fehler beim Beitreten der Gruppe' };
  }
};

// Leave a group
export const leaveGroup = async (groupId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'User not authenticated' };
    
    // Delete membership record
    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', user.id);
      
    if (error) throw error;
    
    // Remove from local cache
    const db = await getDB();
    if (db) {
      await db.delete('group_members', [groupId, user.id]);
    }
    
    return { success: true };
  } catch (error: unknown) {
    console.error('Error leaving group:', error);
    
    // If offline, add to sync queue
    if (!navigator.onLine) {
      try {
        await addToSyncQueue('group_members', 'DELETE', {
          group_id: groupId,
          user_id: (await supabase.auth.getUser()).data.user?.id
        });
        
        return { success: true };
      } catch (syncError) {
        console.error('Error adding leave group to sync queue:', syncError);
      }
    }
    
    return { success: false, error: getErrorMessage(error) || 'Failed to leave group' };
  }
};

// Get members of a specific group
export const getGroupMembers = async (groupId: string): Promise<GroupMember[]> => {
  let storedMembers: GroupMember[] = [];
  
  try {
    // Try to get from IndexedDB first
    const db = await getDB();
    
    if (db) {
      const membersFromDB = await db.getAllFromIndex('group_members', 'by_group_id', groupId) as any[];
      if (membersFromDB && membersFromDB.length > 0) {
        // We need to join with user data to get usernames
        const userPromises = membersFromDB.map((m: any) => db.get('users', m.user_id));
        const users = await Promise.all(userPromises);
        
        storedMembers = membersFromDB.map((m: any, i: number) => ({
          id: m.id,
          group_id: m.group_id,
          user_id: m.user_id,
          username: (users[i] as any)?.username || 'Unknown User',
          joined_at: m.joined_at,
          invited_by: m.invited_by
        }));
        
        // Return early if offline
        if (!navigator.onLine) {
          return storedMembers;
        }
      }
    }
    
    // Get from Supabase with user info
    const { data, error } = await supabase
      .from('group_members')
      .select(`
        *,
        users:user_id(username)
      `)
      .eq('group_id', groupId);
      
    if (error) throw error;
    
    // Format result and cache locally
    const formattedMembers = (data as any[]).map((m: any) => ({
      id: m.id,
      group_id: m.group_id,
      user_id: m.user_id,
      username: m.users?.username || 'Unknown User',
      joined_at: m.joined_at,
      invited_by: m.invited_by
    }));
    
    // Cache in IndexedDB
    if (db && formattedMembers.length > 0) {
      const tx = db.transaction('group_members', 'readwrite');
      const membersStore = tx.objectStore('group_members');
      
      for (const member of formattedMembers) {
        await membersStore.put(member);
      }
      
      await tx.done;
      
      // Also cache user info
      const usersTx = db.transaction('users', 'readwrite');
      const usersStore = usersTx.objectStore('users');
      
      for (const member of formattedMembers) {
        await usersStore.put({
          id: member.user_id,
          username: member.username
        });
      }
      
      await usersTx.done;
    }
    
    return formattedMembers;
  } catch (error) {
    console.error('Error fetching group members:', error);
    
    // Return local data if we have it
    if (storedMembers && storedMembers.length > 0) {
      return storedMembers;
    }
    
    return [];
  }
};

// Get the progress for a group
export const getGroupProgress = async (groupId: string): Promise<GroupProgress[]> => {
  let storedProgress: GroupProgress[] = [];
  
  try {
    // Try to get from local IndexedDB first
    const db = await getDB();
    
    if (db) {
      storedProgress = await db.getAllFromIndex('group_progress', 'by_group_id', groupId) as GroupProgress[];
      
      // Return early if offline
      if (!navigator.onLine) {
        return storedProgress;
      }
    }
    
    // Get from Supabase with user info for who scanned/answered
    const { data, error } = await supabase
      .from('group_progress')
      .select(`
        *,
        scanner:users!first_scanned_by(username),
        answerer:users!first_answered_by(username)
      `)
      .eq('group_id', groupId);
      
    if (error) throw error;
    
    // Format result to match our interface
    const formattedProgress = (data as any[]).map((p: any) => ({
      id: p.id,
      group_id: p.group_id,
      qr_number: p.qr_number,  // Now using qr_number directly from database
      scanned_by: p.first_scanned_by,
      scanned_by_username: p.scanner?.username || null,
      answered_by: p.first_answered_by,
      answered_by_username: p.answerer?.username || null,
      scanned_at: p.first_scanned_at,
      answered_at: p.first_answered_at,
      answer: null // Answers are stored separately in user_answers table
    }));
    
    // Cache in IndexedDB
    if (db && formattedProgress.length > 0) {
      const tx = db.transaction('group_progress', 'readwrite');
      const progressStore = tx.objectStore('group_progress');
      
      for (const progress of formattedProgress) {
        await progressStore.put(progress);
      }
      
      await tx.done;
    }
    
    return formattedProgress;
  } catch (error) {
    console.error('Error fetching group progress:', error);
    
    // Return local data if available
    if (storedProgress && storedProgress.length > 0) {
      return storedProgress;
    }
    
    return [];
  }
};

// Upload a group picture
export const uploadGroupPicture = async (
  groupId: string,
  file: File,
  description: string | null = null
): Promise<{ success: boolean; file_path?: string; error?: string }> => {
  try {
    if (!navigator.onLine) {
      return { success: false, error: 'You must be online to upload images' };
    }
    
    // Call the Edge Function for image validation and getting a signed URL
    const { data, error } = await supabase.functions.invoke('image-validation', {
      body: {
        group_id: groupId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        description
      }
    });
    
    if (error) throw error;
    
    if (!data.success) {
      return { success: false, error: data.error || 'Image validation failed' };
    }
    
    // Upload the file using the signed URL
    const uploadResponse = await fetch(data.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }
    
    return { 
      success: true, 
      file_path: data.file_path
    };
  } catch (error: unknown) {
    console.error('Error uploading group picture:', error);
    return { success: false, error: getErrorMessage(error) || 'Failed to upload image' };
  }
};

// Get group pictures
export const getGroupPictures = async (groupId: string): Promise<GroupPicture[]> => {
  let storedPictures: GroupPicture[] = [];
  
  try {
    // Try to get from IndexedDB first
    const db = await getDB();
    
    if (db) {
      storedPictures = await db.getAllFromIndex('group_pictures', 'by_group_id', groupId) as GroupPicture[];
      
      // Return early if offline
      if (!navigator.onLine) {
        return storedPictures;
      }
    }
    
    // Get from Supabase
    const { data, error } = await supabase
      .from('group_pictures')
      .select('*')
      .eq('group_id', groupId)
      .order('uploaded_at', { ascending: false });
      
    if (error) throw error;
    
    // Cache in IndexedDB
    if (db && data) {
      const tx = db.transaction('group_pictures', 'readwrite');
      const picturesStore = tx.objectStore('group_pictures');
      
      for (const picture of data) {
        await picturesStore.put(picture);
      }
      
      await tx.done;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching group pictures:', error);
    
    // Return local data if available
    if (storedPictures && storedPictures.length > 0) {
      return storedPictures;
    }
    
    return [];
  }
};

// Helper to get the download URL for a group picture
export const getGroupPictureUrl = (filePath: string): string => {
  return supabase.storage.from('group-pictures').getPublicUrl(filePath).data.publicUrl;
};

// Helper function to cache a group's data locally
async function cacheGroupLocally(groupId: string): Promise<void> {
  try {
    // Fetch group data
    const { data: groupData, error: groupError } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .single();
      
    if (groupError) throw groupError;
    
    // Fetch group membership
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    const { data: memberData, error: memberError } = await supabase
      .from('group_members')
      .select('*')
      .eq('group_id', groupId)
      .eq('user_id', user.id)
      .single();
      
    if (memberError) throw memberError;
    
    // Cache in IndexedDB
    const db = await getDB();
    if (db) {
      // Store group
      await db.put('groups', groupData);
      
      // Store membership
      await db.put('group_members', memberData);
    }
  } catch (error) {
    console.error('Error caching group locally:', error);
  }
}

// Record a QR scan for a group
export const recordGroupScan = async (
  groupId: string, 
  questionNumber: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'User not authenticated' };
    
    // First, check if this question has already been scanned for this group
    const { data: existingData, error: existingError } = await supabase
      .from('group_progress')
      .select('*')
      .eq('group_id', groupId)
      .eq('qr_number', parseInt(questionNumber))  // Changed to qr_number
      .maybeSingle();
      
    if (existingError) throw existingError;
    
    // If scan already exists, do nothing
    if (existingData) {
      return { success: true };
    }
    
    // Otherwise, create a new scan record
    const timestamp = new Date().toISOString();
    const scanData = {
      group_id: groupId,
      qr_number: parseInt(questionNumber),  // Changed to qr_number
      scanned_by: user.id,
      scanned_at: timestamp
    };
    
    // Type assertion for the insert operation
    const { data, error } = await supabase
      .from('group_progress')
      .insert(scanData as any) // Use type assertion to bypass TypeScript error
      .select()
      .single();
      
    if (error) throw error;
    
    // Cache in IndexedDB
    const db = await getDB();
    if (db && data) {
      // Get username for display
      const { data: userData } = await supabase
        .from('users')
        .select('username')
        .eq('id', user.id)
        .single();
        
      // Format for storage with type assertion
      const progressData: GroupProgress = {
        ...data as any, // Use type assertion to bypass TypeScript error
        scanned_by_username: (userData as any)?.username || null,
        answered_by_username: null
      };
      
      await db.put('group_progress', progressData);
    }
    
    return { success: true };
  } catch (error: unknown) {
    console.error('Error recording group scan:', error);
    
    // If offline, add to sync queue
    if (!navigator.onLine) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const timestamp = new Date().toISOString();
          await addToSyncQueue('group_progress', 'INSERT', {
            group_id: groupId,
            qr_number: parseInt(questionNumber),  // Changed to qr_number
            scanned_by: user.id,
            scanned_at: timestamp
          });
          
          return { success: true };
        }
      } catch (syncError) {
        console.error('Error adding scan to sync queue:', syncError);
      }
    }
    
    return { success: false, error: getErrorMessage(error) || 'Failed to record scan' };
  }
};

// Submit an answer for a group
// Note: The actual answer text is saved to user_answers table separately.
// This function only updates group_progress to mark the question as answered.
export const submitGroupAnswer = async (
  groupId: string, 
  questionNumber: string, 
  _answer: string  // Prefixed with _ to indicate intentionally unused - answer is saved via user_answers table
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'User not authenticated' };
    
    // First, check if this question has been scanned for this group
    const { data: existingData, error: existingError } = await supabase
      .from('group_progress')
      .select('*')
      .eq('group_id', groupId)
      .eq('qr_number', parseInt(questionNumber))  // Changed to qr_number
      .maybeSingle();
      
    if (existingError) throw existingError;
    
    // If no scan exists, can't answer
    if (!existingData) {
      return { success: false, error: 'Question must be scanned before answering' };
    }
    
    // Update the record with answer info (not the answer text itself - that's in user_answers)
    const timestamp = new Date().toISOString();
    
    // Cast to any to bypass TypeScript issue with Supabase types
    const updateQuery = (supabase.from('group_progress') as any)
      .update({
        answered: true,
        first_answered_by: (existingData as any).first_answered_by || user.id,
        first_answered_at: (existingData as any).first_answered_at || timestamp
      })
      .eq('id', (existingData as any).id)
      .select()
      .single();
    
    const { data, error } = await updateQuery;
      
    if (error) throw error;
    
    // Cache in IndexedDB
    const db = await getDB();
    if (db && data) {
      // Get username for display
      const { data: userData } = await supabase
        .from('users')
        .select('username')
        .eq('id', user.id)
        .single();
        
      // Format for storage
      const progressData: GroupProgress = {
        ...data as any, // Use type assertion to bypass TypeScript error
        answered_by_username: (userData as any)?.username || null
      };
      
      await db.put('group_progress', progressData);
    }
    
    return { success: true };
  } catch (error: unknown) {
    console.error('Error submitting group answer:', error);
    
    // If offline, add to sync queue
    if (!navigator.onLine) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const timestamp = new Date().toISOString();
          await addToSyncQueue('group_progress', 'UPDATE', {
            group_id: groupId,
            qr_number: parseInt(questionNumber),
            answered: true,
            first_answered_by: user.id,
            first_answered_at: timestamp
            // Note: actual answer text is saved to user_answers table separately
          });
          
          return { success: true };
        }
      } catch (syncError) {
        console.error('Error adding answer to sync queue:', syncError);
      }
    }
    
    return { success: false, error: getErrorMessage(error) || 'Failed to submit answer' };
  }
};